import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { Boxes, RefreshCw, Server, Users, Wallet } from 'lucide-react'
import ServerDiagram from '@/components/landing/server-diagram'
import SectionHeading, { Accent } from '@/components/landing/section-heading'
import { SERVER_FEATURES } from '@/constants/kinematic'

const FEATURE_ICONS: LucideIcon[] = [Server, Boxes, Wallet, RefreshCw]

interface FlowCardProps {
    icon: LucideIcon
    title: string
    subtitle: string
    children: ReactNode
}

const FlowCard: FC<FlowCardProps> = ({ icon: Icon, title, subtitle, children }) => {
    return (
        <div className='rounded-[22px] border border-white/[0.08] bg-ink-1 p-4'>
            <div className='flex items-center gap-3'>
                <span className='grid size-9 place-items-center rounded-xl bg-white/[0.04] text-ember'>
                    <Icon aria-hidden='true' className='size-4' />
                </span>
                <div>
                    <div className='text-base font-semibold text-bone'>{title}</div>
                    <div className='font-mono text-[10px] text-steel'>{subtitle}</div>
                </div>
            </div>
            <div className='mt-4'>
                {children}
            </div>
        </div>
    )
}

const Connector: FC = () => {
    return (
        <div aria-hidden='true' className='relative mx-auto h-8 w-px bg-white/15'>
            <span className='absolute -left-[2.5px] top-0 size-1.5 animate-drip rounded-full bg-ember motion-reduce:hidden' />
        </div>
    )
}

const Chip: FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <span className='rounded-lg border border-white/[0.07] bg-white/[0.03] px-2.5 py-1.5 font-mono text-[11px] text-bone/85'>
            {children}
        </span>
    )
}

const ServerSection: FC = () => {
    return (
        <section id='server' aria-labelledby='server-title' className='relative overflow-hidden py-24 md:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute left-1/2 top-1/3 h-[480px] w-[min(1000px,90vw)] -translate-x-1/2 rounded-full bg-ember/[0.06] blur-[120px]' />

            <div className='relative mx-auto max-w-[1320px] px-5 md:px-8'>
                <SectionHeading
                    id='server-title'
                    index='03'
                    label='Your server'
                    title={<>Your game server runs in your own <Accent>Cloudflare</Accent> account.</>}
                    lead='It is one Worker with one actor for each player and currency, and your wallet stays the only spend authority.'
                />

                <div className='mt-14 hidden rounded-[32px] border border-white/[0.07] bg-[#0a0a0c]/80 p-6 shadow-[0_60px_140px_-70px_rgba(255,91,31,0.35)] lg:block xl:p-10'>
                    <ServerDiagram />

                    <div className='mt-6 flex flex-wrap items-center gap-6 border-t border-white/[0.06] pt-5 font-mono text-[11px] text-steel'>
                        <span className='flex items-center gap-2'>
                            <span className='size-2 rounded-full bg-ember' />
                            Bets and stakes
                        </span>
                        <span className='flex items-center gap-2'>
                            <span className='size-2 rounded-full bg-mint' />
                            Payouts carrying the proof
                        </span>
                        <span className='ml-auto'>Your keys, your limits, your logs</span>
                    </div>
                </div>

                <div className='mt-12 lg:hidden'>
                    <FlowCard icon={Users} title='Players' subtitle='One session per currency'>
                        <div className='flex flex-wrap gap-1.5'>
                            <Chip>player_48213 · USDT</Chip>
                            <Chip>player_10027 · SOL</Chip>
                            <Chip>player_77310 · USDC</Chip>
                        </div>
                    </FlowCard>

                    <Connector />

                    <div className='rounded-[26px] border border-dashed border-ember/40 bg-ember/[0.025] p-3'>
                        <div className='px-1 pb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-ember'>
                            Your Cloudflare account
                        </div>

                        <FlowCard icon={Server} title='Game server' subtitle='One Worker, your hostname'>
                            <ul className='grid gap-1.5 text-sm text-bone/80'>
                                <li>Serves the games and accepts every bet</li>
                                <li>Proves outcomes with your own VRF key</li>
                                <li>Signs every record</li>
                            </ul>
                        </FlowCard>

                        <Connector />

                        <FlowCard icon={Boxes} title='Durable Objects' subtitle='One actor per player and currency'>
                            <div className='flex flex-wrap gap-1.5'>
                                <Chip>48213 · USDT</Chip>
                                <Chip>10027 · SOL</Chip>
                                <Chip>77310 · USDC</Chip>
                            </div>
                        </FlowCard>
                    </div>

                    <Connector />

                    <FlowCard icon={Wallet} title='Your wallet' subtitle='The only spend authority'>
                        <div className='flex flex-wrap gap-1.5'>
                            <Chip>debit</Chip>
                            <Chip>credit with proof</Chip>
                            <Chip>balance</Chip>
                        </div>
                    </FlowCard>
                </div>

                <div className='mt-4 grid gap-px overflow-hidden rounded-[28px] border border-white/[0.07] bg-white/[0.07] md:grid-cols-2 lg:mt-6 lg:grid-cols-4'>
                    {SERVER_FEATURES.map((feature, index) => {
                        const Icon = FEATURE_ICONS[index] ?? Server

                        return (
                            <div key={feature.title} className='bg-ink p-7'>
                                <div className='flex items-center justify-between'>
                                    <Icon aria-hidden='true' className='size-5 text-ember' />
                                    <span className='font-mono text-[10px] text-steel/60'>0{index + 1}</span>
                                </div>
                                <h3 className='mt-8 text-lg font-semibold tracking-[-0.01em] text-bone'>
                                    {feature.title}
                                </h3>
                                <p className='mt-2 text-sm leading-relaxed text-steel'>
                                    {feature.text}
                                </p>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default ServerSection
